import React, { useState, useEffect, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { TIPO_UBICACION, ESTADOS_US } from "@/lib/constants"; // Fix P (2026-10-13): estados para la dirección separada
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Search, Plus, Pencil, Loader2, MapPin, TrainFront, Warehouse, Container, Truck } from "lucide-react";
const ICONO_UBIC = { riel: TrainFront, yarda: Warehouse, deposito_vacios: Container, alquiler_chasis: Truck };
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { t } from "@/lib/i18n";

export default function Ubicaciones() {
  const [ubic, setUbic] = useState([]);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const cargar = useCallback(async () => {
    const { data } = await api.get(`/ubicaciones?search=${encodeURIComponent(search)}`);
    setUbic(data);
  }, [search]);

  useEffect(() => { const tt = setTimeout(cargar, 200); return () => clearTimeout(tt); }, [cargar]);

  const abrir = (u) => { setEditing(u); setOpen(true); };

  return (
    <div className="p-4 lg:p-8 space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="font-display font-bold text-[26px] lg:text-[30px] leading-[1.05] text-ink" data-testid="page-title">{t("Ubicaciones")}</h1>
        <Button data-testid="ubicacion-nueva-btn" className="bg-brand hover:bg-brand-dark text-white" onClick={() => abrir(null)}><Plus className="h-4 w-4 mr-2" />{t(" Nueva ubicación")}</Button>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-3" />
        <Input data-testid="ubicacion-buscador" placeholder={t("Buscar ubicación…")} value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-10 bg-white" />
      </div>

      <div className="bg-white rounded-xl border overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-surface-2 text-left eyebrow">
              <th className="px-4 py-3">{t("Nombre")}</th>
              <th className="px-4 py-3">{t("Tipo")}</th>
              <th className="px-4 py-3">{t("Dirección")}</th>
              <th className="px-4 py-3">{t("Estado")}</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#EFECE6]">
            {ubic.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-ink-3">{t("Sin ubicaciones")}</td></tr>}
            {ubic.map((u) => (
              <tr key={u.id} data-testid={`ubicacion-fila-${u.id}`} className={cn("hover:bg-surface-2", !u.activo && "opacity-50")}>
                <td className="px-4 py-3 font-semibold text-ink">{u.nombre}</td>
                <td className="px-4 py-3"><span className="inline-flex items-center gap-1.5 rounded-md bg-surface-2 border border-line px-2 py-1 text-[13px] text-ink-2">{React.createElement(ICONO_UBIC[u.tipo] || MapPin, { className: "h-3.5 w-3.5" })}{TIPO_UBICACION[u.tipo]}</span>{u.yarda_propia && <span data-testid={`yarda-propia-${u.id}`} className="ml-2 text-[12.5px] font-semibold text-navy-2">{t("Yarda propia")}</span>}</td>
                <td className="px-4 py-3 text-ink-2">{u.direccion || "—"}</td>
                <td className="px-4 py-3">{u.activo ? <span className="text-[#277A3F] font-medium">{t("Activo")}</span> : <span className="text-ink-3">{t("Inactivo")}</span>}</td>
                <td className="px-4 py-3 text-right"><Button data-testid={`ubicacion-editar-${u.id}`} variant="ghost" size="sm" onClick={() => abrir(u)}><Pencil className="h-4 w-4" /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && <UbicacionDialog ubicacion={editing} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); cargar(); }} />}
    </div>
  );
}

function UbicacionDialog({ ubicacion, onClose, onSaved }) {
  const [nombre, setNombre] = useState(ubicacion?.nombre || "");
  const [tipo, setTipo] = useState(ubicacion?.tipo || "riel");
  // Fix P (2026-10-13): dirección separada. Una ubicación vieja que solo tiene la dirección en un texto la muestra en «Calle» para separarla.
  const sinPartes = !ubicacion?.calle && !ubicacion?.ciudad && !ubicacion?.estado_us && !ubicacion?.zip;
  const [calle, setCalle] = useState(ubicacion?.calle || (sinPartes ? ubicacion?.direccion || "" : ""));
  const [ciudad, setCiudad] = useState(ubicacion?.ciudad || "");
  const [estadoUs, setEstadoUs] = useState(ubicacion?.estado_us || "");
  const [zip, setZip] = useState(ubicacion?.zip || "");
  const [yardaPropia, setYardaPropia] = useState(!!ubicacion?.yarda_propia);
  const [notas, setNotas] = useState(ubicacion?.notas || "");
  const [activo, setActivo] = useState(ubicacion ? ubicacion.activo : true);
  const [saving, setSaving] = useState(false);

  const guardar = async (e) => {
    e.preventDefault();
    if (zip && !/^\d{5}(-\d{4})?$/.test(zip.trim())) { toast.error(t("El ZIP debe tener 5 dígitos")); return; }
    setSaving(true);
    try {
      // direccion va vacía a propósito: el servidor la arma con calle, ciudad, estado y ZIP.
      const payload = { nombre, tipo, direccion: "", calle, ciudad, estado_us: estadoUs, zip, notas: notas.trim(), yarda_propia: tipo === "yarda" && yardaPropia, activo };
      if (ubicacion) await api.put(`/ubicaciones/${ubicacion.id}`, payload);
      else await api.post("/ubicaciones", payload);
      toast.success(ubicacion ? t("Ubicación actualizada") : t("Ubicación creada"));
      onSaved();
    } catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white">
        <DialogHeader><DialogTitle>{ubicacion ? t("Editar ubicación") : t("Nueva ubicación")}</DialogTitle></DialogHeader>
        <form onSubmit={guardar} className="space-y-4">
          <div><Label className="eyebrow">{t("Nombre *")}</Label><Input data-testid="ubicacion-nombre-input" value={nombre} onChange={(e) => setNombre(e.target.value)} className="mt-1 h-11" required /></div>
          <div>
            <Label className="eyebrow">{t("Tipo *")}</Label>
            <Select value={tipo} onValueChange={setTipo}>
              <SelectTrigger data-testid="ubicacion-tipo-select" className="h-11 bg-white mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {Object.entries(TIPO_UBICACION).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label className="eyebrow">{t("Calle y número")}</Label><Input data-testid="ubicacion-calle-input" value={calle} onChange={(e) => setCalle(e.target.value)} className="mt-1 h-11" /></div>
          <div className="grid grid-cols-[1.4fr_.8fr_.8fr] gap-3">
            <div><Label className="eyebrow">{t("Ciudad")}</Label><Input data-testid="ubicacion-ciudad-input" value={ciudad} onChange={(e) => setCiudad(e.target.value)} className="mt-1 h-11" /></div>
            <div>
              <Label className="eyebrow">{t("State")}</Label>
              <Select value={estadoUs || "__sin__"} onValueChange={(v) => setEstadoUs(v === "__sin__" ? "" : v)}>
                <SelectTrigger data-testid="ubicacion-estado-select" className="h-11 bg-white mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white max-h-64">
                  <SelectItem value="__sin__">—</SelectItem>
                  {ESTADOS_US.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="eyebrow">{t("ZIP")}</Label><Input data-testid="ubicacion-zip-input" value={zip} onChange={(e) => setZip(e.target.value)} inputMode="numeric" maxLength={10} className="mt-1 h-11" /></div>
          </div>
          <div>
            <Label htmlFor="ubicacion-notas" className="eyebrow">{t("Notas de la ubicación")}</Label>
            <Textarea id="ubicacion-notas" data-testid="ubicacion-notas-input" value={notas} onChange={(e) => setNotas(e.target.value)} maxLength={500} rows={3} className="mt-1" />
            <p className="mt-1 text-xs text-ink-3">{t("Visibles para el chofer antes de llegar.")}</p>
          </div>
          {tipo === "yarda" && <label className="flex items-center gap-2 text-sm text-ink-2"><input data-testid="ubicacion-yarda-propia-check" type="checkbox" checked={yardaPropia} onChange={(e) => setYardaPropia(e.target.checked)} className="h-4 w-4" />{t(" Yarda propia (Leona's yard)")}</label>}
          {ubicacion && <label className="flex items-center gap-2 text-sm text-ink-2"><input data-testid="ubicacion-activo-check" type="checkbox" checked={activo} onChange={(e) => setActivo(e.target.checked)} className="h-4 w-4" />{t(" Activo")}</label>}
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose}>{t("Cancelar")}</Button>
            <Button type="submit" data-testid="ubicacion-guardar" className="bg-brand hover:bg-brand-dark text-white" disabled={saving}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Guardar")}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
